import type { AxiosError, AxiosResponse } from 'axios';
import type { CustomAxiosRequestConfig } from '../notifications';
import { getErrorTitle } from './error-title';
import { showApiErrorNotification, showSuccessNotification } from './notifications';

export interface NotificationHandlerOptions {
  /**
   * Mantine notifications instance
   */
  notifications: any;

  /**
   * Optional callback for handling errors (e.g., Sentry logging)
   */
  onError?: (error: AxiosError) => void;

  /**
   * Optional callback for handling successful responses
   */
  onSuccess?: (response: AxiosResponse) => void;

  /**
   * URL paths to ignore for success notifications
   */
  ignoredPaths?: string[];
}

/**
 * Creates a standardized error handler for axios interceptors
 */
export function createErrorHandler(options: NotificationHandlerOptions) {
  const { notifications, onError } = options;

  return (error: AxiosError) => {
    // Show a connection error notification for aborted requests or network failures
    if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
      notifications.show({
        title: 'Connection Error',
        message: 'Unable to reach the server. Please check your connection and try again.',
        color: 'red',
        autoClose: 10000,
      });
      return;
    }

    // Call optional error callback (for Sentry, logging, etc.)
    if (onError) {
      onError(error);
    }

    // Check if custom error notification is provided in the request config
    const config = error.config as CustomAxiosRequestConfig;
    const customNotification = config?.notification;

    if (customNotification && 'onError' in customNotification && customNotification.onError) {
      // Use custom error notification if provided
      showApiErrorNotification(
        error,
        notifications,
        customNotification.onError.title || getErrorTitle(error)
      );
    } else {
      // Use default error notification
      const title = getErrorTitle(error);
      showApiErrorNotification(error, notifications, title);
    }
  };
}

/**
 * Creates a standardized success handler for axios interceptors
 */
export function createSuccessHandler(options: NotificationHandlerOptions) {
  const { notifications, onSuccess, ignoredPaths = [] } = options;

  return (response: AxiosResponse) => {
    const accepted = ['post', 'put', 'patch'];
    if (!accepted.includes(response.config.method || '')) {
      return;
    }

    // Check if this path should be ignored
    if (ignoredPaths.some((path) => response.config.url?.includes(path))) {
      return;
    }

    // Call optional success callback
    if (onSuccess) {
      onSuccess(response);
    }

    // Check if custom notification is provided in the request config
    const config = response.config as CustomAxiosRequestConfig;
    const customNotification = config?.notification;

    if (customNotification) {
      if ('onSuccess' in customNotification) {
        // Structured notification config
        if (customNotification.onSuccess !== false && customNotification.onSuccess) {
          const successNotif = customNotification.onSuccess;
          showSuccessNotification(successNotif.title, successNotif.message, notifications);
        }
        // If onSuccess is false, don't show any notification
      } else {
        // Simple notification config (backward compatibility)
        const simpleNotif = customNotification as { title: string; message: string };
        showSuccessNotification(simpleNotif.title, simpleNotif.message, notifications);
      }
    } else {
      // Show default notification
      showSuccessNotification(
        `Request Successful`,
        `Your ${response.config.method?.toUpperCase()} request to ${response.config.url} was successful.`,
        notifications
      );
    }
  };
}
